/* ----------------------------------------------------------------------------------------------------------------
   RATING FEEDBACK FORM
   ----------------------------------------------------------------------------------------------------------------
   Behaviour for sections/rating-feedback-form.liquid. Every setting reaches this
   file as a data attribute, so it never needs Liquid and can be cached and
   deferred.
   ---------------------------------------------------------------------------------------------------------------- */

(function () {
if (customElements.get('rating-feedback-form')) return;

var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

// Every value the theme can post to a Google Form. In a pre-filled link, a box
// holding one of these names is a field; any other value is a constant.
var PREFILL_KEYS = ['response_id', 'rating', 'rating_label', 'rating_scale', 'answers_text', 'answers_json',
  'rating_feedback', 'feedback_tags', 'additional_feedback', 'email', 'phone', 'marketing', 'reference',
  'invite_id', 'customer_id', 'market', 'signature', 'page', 'submitted_at', 'body'];

// The mouth's resting smile: a gentle one, well short of a 10.
var REST_SMILE = 0.55;
var toArray = function (list) { return Array.prototype.slice.call(list); };

// 0 is a real rating on a 0 – 10 scale, so "nothing chosen" is null, never 0.
var isRating = function (value) { return typeof value === 'number' && !isNaN(value); };

class RatingFeedbackForm extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    /* A custom element upgrades the moment its opening tag is parsed, which can
       be before its own children exist. With a deferred script that never
       happens, but the file should not depend on how it was loaded. */
    if (document.readyState === 'loading' && !this.querySelector('[data-viewport]')) {
      var el = this;
      document.addEventListener('DOMContentLoaded', function () { el.mount(); }, { once: true });
      return;
    }

    this.mount();
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    clearTimeout(this._advance);
    clearTimeout(this._settle);
    clearTimeout(this._rest);
  }

  /* ------------------------------------------------------------ setup */
  mount() {
    this.form = this.querySelector('.rating-feedback__form');

    /* Shopify injects a "form persister" into every page. It matches
       form[action*='/contact']:not([data-nocaptcha='true']), cancels the submit
       event, saves the fields to sessionStorage, then calls the *native*
       HTMLFormElement.prototype.submit — which fires no submit event, so no
       listener of ours can stop it. The page then navigates to /contact and
       Shopify's spam protection shows a captcha. This attribute is that
       script's own opt-out, and we set it before DOMContentLoaded, which is
       when it binds. We never submit this form natively; it is posted with
       fetch, so there is nothing to persist. */
    if (this.form) this.form.setAttribute('data-nocaptcha', 'true');
    this.viewport = this.querySelector('[data-viewport]');
    this.bar = this.querySelector('[data-bar]');
    this.stepCount = this.querySelector('[data-step-count]');
    this.progress = this.querySelector('[data-progress]');
    this.face = this.querySelector('[data-face]');
    this.reaction = this.querySelector('[data-reaction]');
    this.options = toArray(this.querySelectorAll('.rating-feedback__option'));
    this.radios = toArray(this.querySelectorAll('[data-rating-input]'));

    var steps = toArray(this.querySelectorAll('[data-step]'));
    this.ratingStep = steps.filter(function (s) { return s.dataset.step === 'rating'; })[0];
    this.questionSteps = steps.filter(function (s) { return s.dataset.step === 'question'; });
    this.extraStep = steps.filter(function (s) { return s.dataset.step === 'extra'; })[0] || null;
    this.successStep = steps.filter(function (s) { return s.dataset.step === 'success'; })[0];
    this.expiredStep = steps.filter(function (s) { return s.dataset.step === 'expired'; })[0] || null;
    this.allSteps = steps;

    this.scale = parseInt(this.dataset.scale, 10) || 5;
    // 0 – 5 and 0 – 10 start at nothing; the others at 1. this.scale stays the top.
    this.min = this.dataset.min === '0' ? 0 : 1;
    this.stepTotal = parseInt(this.dataset.stepTotal, 10) || 3;
    this.maxLength = parseInt(this.dataset.maxLength, 10) || 500;
    this.autoAdvance = this.dataset.autoAdvance !== 'false';
    this.advanceDelay = parseInt(this.dataset.advanceDelay, 10);
    if (isNaN(this.advanceDelay)) this.advanceDelay = 400;
    this.touchDelay = parseInt(this.dataset.touchDelay, 10);
    if (isNaN(this.touchDelay)) this.touchDelay = 700;
    this.emailRequired = this.dataset.emailRequired === 'true';
    this.method = this.dataset.method || 'none';
    this.endpoint = this.dataset.endpoint || '';
    this.googleForm = this.safeFormUrl(this.dataset.googleForm);
    this.endpointMode = this.dataset.endpointMode || 'json';
    // A pasted pre-filled link already carries Google's own field ids, so the
    // map can build itself. Anything typed by hand still wins over it.
    this.googleMap = Object.assign(
      this.mapFromPrefill(this.dataset.googlePrefill),
      this.parseEntryMap(this.dataset.googleMap)
    );
    this.googleStatic = this.parseStaticFields(this.dataset.googleStatic);
    this.googleFormGlobal = this.safeFormUrl(this.dataset.googleFormGlobal);
    this.googleMapGlobal = Object.assign(
      this.mapFromPrefill(this.dataset.googlePrefillGlobal),
      this.parseEntryMap(this.dataset.googleMapGlobal)
    );
    this.market = this.dataset.market || 'india';
    this.customerId = this.dataset.customerId || '';
    this.shopperCountry = this.dataset.country || 'IN';
    this.cartPrefix = this.dataset.cartPrefix || 'Feedback';
    this.alsoEmail = this.dataset.alsoEmail === 'true';
    this.labels = (this.dataset.scaleLabels || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);

    // The face follows Net Promoter Score: 9–10 happy, 7–8 neutral, the rest
    // sad. A 1–5 scale scales that and rounds up, giving 5, 4 and 1–3.
    this.happyFrom = Math.ceil((9 * this.scale) / 10);
    this.neutralFrom = Math.ceil((7 * this.scale) / 10);

    // Where the face sits before any rating, and the point it settles back to.
    this.restMouth = this.dataset.restSmile === 'true' ? REST_SMILE : 0;
    this._mouth = this.restMouth;

    // A mangled link in the setting must cost the early rating, never the form.
    try {
      this.capture = this.dataset.capture === 'true' ? this.captureTarget(this.dataset.capturePrefill) : null;
    } catch (error) {
      this.capture = null;
    }

    this.spamProtection = this.dataset.spamProtection !== 'false';
    this.minSubmitMs = (parseInt(this.dataset.minSeconds, 10) || 0) * 1000;
    this.rateLimitMs = (parseInt(this.dataset.rateMinutes, 10) || 0) * 60000;
    this.mountedAt = Date.now();
    this.timeout = parseInt(this.dataset.timeout, 10) || 15000;
    this.counterDisplay = this.dataset.counterDisplay || 'near';
    this.inviteEnabled = this.dataset.invite === 'true';
    this.inviteSecret = this.dataset.inviteSecret || '';
    this.verifySecret = this.dataset.verifySecret || '';
    this.inviteRequired = this.dataset.inviteRequired === 'true';
    this.invitePrefill = this.dataset.invitePrefill !== 'false';
    this.emailParam = (this.dataset.emailParam || '').trim();
    this.hideKnownEmail = this.dataset.hideLinkEmail === 'true';
    this.resumeEnabled = this.dataset.resume !== 'false';
    this.perScreen = parseInt(this.dataset.perScreen, 10) || 1;
    this.mergeClosing = this.dataset.mergeClosing === 'true';
    this.showSummary = this.dataset.showSummary !== 'false';
    this.compactHeader = this.dataset.headerDisplay !== 'all';
    this.storageKey = 'rating-feedback:' + (this.id || 'section');

    this.started = false;
    this.busy = false;
    this.flow = this.defaultFlow();
    this.pos = 0;

    this.groupBlocks();
    this.hydrateQuestions();
    this.setupPhones();
    this.decorateRadios();
    this.labelButtons();
    this.bindEvents();
    this.updateProgress();

    this.classList.add('is-ready');
    if (this.getBoundingClientRect().top < window.innerHeight) this.classList.add('is-inview');
    this.observeViewport();
    this.setupEditorPreview();
    this.readEmailParam();
    this.startInvite();
    this.resumeAnonymous();
    this.catchUp();
  }

  /* The radios are real inputs, so a tap that lands before this script does
     still registers. Pick it up rather than leaving the customer stuck. */
  catchUp() {
    if (this.pos !== 0 || this.classList.contains('is-verifying')) return;
    var early = this.state();
    if (!isRating(early)) return;

    this.ensureResponseId();
    this.paint(early);
    if (this.autoAdvance) this.next();
  }

  defaultFlow() {
    var flow = [this.ratingStep];
    if (this.extraStep) flow.push(this.extraStep);
    flow.push(this.successStep);
    return flow;
  }

  /* Merchants decide how many blocks share a screen: one each, two at a time,
     or everything after the rating on a single page. Blocks are merged inside
     their own branch, never across ratings. */
  groupBlocks() {
    if (this.perScreen <= 1 && !this.mergeClosing) return;

    var el = this;
    var size = Math.max(1, this.perScreen);
    var branches = toArray(this.querySelectorAll('[data-branch]'));
    var scopes = branches.length ? branches : [this.viewport];

    scopes.forEach(function (scope) {
      var steps = toArray(scope.querySelectorAll('[data-step="question"]'));
      if (!steps.length) return;

      for (var i = 0; i < steps.length; i += size) {
        var group = steps.slice(i, i + size);
        // a lone block still becomes a page when the closing question may join it
        if (group.length > 1 || el.mergeClosing) el.mergeSteps(group);
      }
    });

    this.questionSteps = toArray(this.querySelectorAll('[data-step="question"]'));
  }

  /* Turns several step sections into panels of one page, keeping only the last
     set of buttons. */
  mergeSteps(group) {
    var page = document.createElement('section');
    page.className = 'rating-feedback__step';
    page.dataset.step = 'question';
    page.dataset.page = 'true';
    page.dataset.key = group.map(function (step) { return step.dataset.key; }).join('+');
    group[0].parentNode.insertBefore(page, group[0]);

    group.forEach(function (step, index) {
      step.classList.remove('rating-feedback__step');
      step.classList.add('rating-feedback__panel');
      step.removeAttribute('data-step');
      step.dataset.panel = 'true';

      if (index < group.length - 1) {
        var actions = step.querySelector('.rating-feedback__actions');
        if (actions) actions.remove();
      }

      page.appendChild(step);
    });

    // page-level messages (a failed send, a repeat submission) belong beside the
    // button, not under whichever question happens to come first
    var notice = document.createElement('p');
    notice.className = 'rating-feedback__error';
    notice.setAttribute('role', 'alert');
    notice.setAttribute('data-error', '');
    notice.setAttribute('data-page-error', '');

    page.appendChild(notice);

    return page;
  }

  /* The closing question can ride along on the last screen instead of taking one
     of its own. It moves rather than copies, so it is never in two places. */
  absorbExtra(page) {
    if (!this.extraStep || !page) return;

    this.restoreActions();
    page.querySelectorAll('.rating-feedback__actions').forEach(function (actions) { actions.hidden = true; });

    this.extraStep.classList.remove('rating-feedback__step');
    this.extraStep.classList.add('rating-feedback__panel');
    this.extraStep.dataset.panel = 'true';
    page.appendChild(this.extraStep);
  }

  /* Puts it back on a screen of its own, for when the customer switches to a
     rating whose branch has no questions to attach it to. */
  releaseExtra() {
    this.restoreActions();
    if (!this.extraStep || !this.extraStep.dataset.panel) return;

    this.extraStep.classList.add('rating-feedback__step');
    this.extraStep.classList.remove('rating-feedback__panel');
    this.extraStep.removeAttribute('data-panel');
    this.viewport.insertBefore(this.extraStep, this.successStep);
  }

  restoreActions() {
    this.questionSteps.forEach(function (step) {
      step.querySelectorAll('.rating-feedback__actions').forEach(function (actions) { actions.hidden = false; });
    });
  }

  /* One step may hold several blocks. */
  panelsOf(step) {
    var panels = step.querySelectorAll('[data-panel]');
    return panels.length ? toArray(panels) : [step];
  }

  /* Question blocks are portable, so the section hands them its own
     settings (limits, labels, which controls to keep) at runtime. */
  hydrateQuestions() {
    var el = this;
    var showCounter = this.counterDisplay !== 'never';
    var showBack = this.dataset.showBack !== 'false';
    var skipLabel = (this.dataset.skipLabel || '').trim();

    this.questionSteps.forEach(function (step) {
      var branch = step.closest('[data-branch]');
      var low = branch ? parseInt(branch.dataset.min, 10) : NaN;
      var high = branch ? parseInt(branch.dataset.max, 10) : NaN;
      step.rfMin = isNaN(low) ? el.min : low;
      step.rfMax = isNaN(high) ? el.scale : high;

      el.panelsOf(step).forEach(function (panel) {
        var field = panel.querySelector('[data-feedback]');
        if (field) field.setAttribute('maxlength', el.maxLength);

        var counter = panel.querySelector('[data-counter]');
        if (!counter) return;
        if (!showCounter || !field) counter.remove();
        else counter.textContent = '0 / ' + el.maxLength;
      });

      var back = step.querySelector('[data-back]');
      if (back) {
        if (!showBack) back.remove();
        else back.querySelector('[data-label-back]').textContent = el.dataset.backLabel || '';
      }

      var skip = step.querySelector('[data-skip]');
      if (skip) {
        var panels = el.panelsOf(step);
        var mandatory = panels.some(function (panel) {
          return panel.dataset.required === 'true' || (parseInt(panel.dataset.minChips, 10) || 0) > 0;
        });
        var answerable = panels.some(function (panel) { return panel.querySelector('[data-feedback]'); });
        if (!skipLabel || mandatory || !answerable) skip.remove();
        else skip.querySelector('[data-label-skip]').textContent = skipLabel;
      }
    });

    /* Branches set up for a 1 – 10 scale start at 1. Switching to 0 – 10 must
       not leave a 0 with no follow-up question, so when no branch claims 0,
       the ones that start at 1 take it too. A branch set to start at 0 turns
       this off. */
    if (this.min === 0 && !this.questionSteps.some(function (step) { return step.rfMin <= 0; })) {
      this.questionSteps.forEach(function (step) { if (step.rfMin === 1) step.rfMin = 0; });
    }
  }

  setupPhones() {
    var el = this;
    this.phones = toArray(this.querySelectorAll('[data-phone]'));

    this.phones.forEach(function (phone) {
      phone.rfCountries = (phone.dataset.countryData || '').split('|').map(function (row) {
        var parts = row.split(':');
        if (parts.length < 3) return null;
        return { iso: parts[0].trim().toUpperCase(), name: parts[1].trim(), dial: parts[2].trim() };
      }).filter(Boolean);

      var start = phone.rfCountries.filter(function (c) { return c.iso === el.shopperCountry; })[0]
        || phone.rfCountries[0];

      el.setDial(phone, start);
      el.bindPhone(phone);
    });
  }

  /* Flag emoji straight from the ISO code — no image files involved. */
  flagFor(iso) {
    return iso.replace(/./g, function (character) {
      return String.fromCodePoint(127397 + character.charCodeAt(0));
    });
  }

  setDial(phone, country) {
    if (!country) return;
    phone.rfCountry = country;
    var flag = phone.querySelector('[data-dial-flag]');
    var code = phone.querySelector('[data-dial-code]');
    if (flag) flag.textContent = this.flagFor(country.iso);
    if (code) code.textContent = '+' + country.dial;
    this.syncPhone(phone);
  }

  /* The visible field holds the national number; the hidden one holds what
     actually gets submitted. */
  syncPhone(phone) {
    var input = phone.querySelector('[data-phone-input]');
    var hidden = phone.querySelector('[data-feedback]');
    if (!input || !hidden) return;

    var digits = input.value.replace(/\D/g, '');
    hidden.value = digits ? '+' + phone.rfCountry.dial + ' ' + digits : '';
  }

  bindPhone(phone) {
    var el = this;
    var button = phone.querySelector('[data-dial]');
    var panel = phone.querySelector('[data-country-panel]');
    var search = phone.querySelector('[data-country-search]');
    var list = phone.querySelector('[data-country-list]');
    var empty = phone.querySelector('[data-country-empty]');
    var input = phone.querySelector('[data-phone-input]');

    if (input) {
      input.addEventListener('input', function () {
        el.syncPhone(phone);
        el.hideError(phone.closest('[data-step]'));
        clearTimeout(el._saveTimer);
        el._saveTimer = setTimeout(function () { el.saveProgress(); }, 400);
      });
    }

    var render = function (term) {
      var needle = (term || '').trim().toLowerCase();
      var matches = phone.rfCountries.filter(function (country) {
        return !needle || country.name.toLowerCase().indexOf(needle) > -1 ||
          country.dial.indexOf(needle.replace('+', '')) === 0 ||
          country.iso.toLowerCase() === needle;
      });

      list.innerHTML = '';
      matches.forEach(function (country) {
        var item = document.createElement('li');
        var option = document.createElement('button');
        option.type = 'button';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', country.iso === phone.rfCountry.iso ? 'true' : 'false');
        option.innerHTML = '<span aria-hidden="true">' + el.flagFor(country.iso) + '</span>' +
          '<span class="rf-country-name"></span><span class="rf-country-code">+' + country.dial + '</span>';
        option.querySelector('.rf-country-name').textContent = country.name;
        option.addEventListener('click', function () {
          el.setDial(phone, country);
          close();
          if (input) input.focus();
        });
        item.appendChild(option);
        list.appendChild(item);
      });

      if (empty) empty.hidden = matches.length > 0;
    };

    var close = function () {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', outside, true);
    };

    var outside = function (event) {
      if (!phone.contains(event.target)) close();
    };

    var open = function () {
      render('');
      if (search) search.value = '';
      panel.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', outside, true);
      if (search) setTimeout(function () { search.focus(); }, 30);
    };

    button.addEventListener('click', function () {
      if (panel.hidden) open();
      else close();
    });

    if (search) {
      search.addEventListener('input', function () { render(search.value); });
    }

    phone.addEventListener('keydown', function (event) {
      if (panel.hidden) return;

      var options = toArray(list.querySelectorAll('button'));
      var index = options.indexOf(document.activeElement);

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        button.focus();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        (options[index + 1] || options[0]).focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        (options[index - 1] || options[options.length - 1]).focus();
      }
    });
  }

  decorateRadios() {
    var el = this;
    this.options.forEach(function (option) {
      var srLabel = option.querySelector('[data-sr-label]');
      if (!srLabel) return;
      var value = parseInt(option.dataset.value, 10);
      srLabel.textContent = el.fillTemplate(el.dataset.ratingLabelTemplate, value, el.labelFor(value));
    });
  }

  bindEvents() {
    var el = this;

    this.addEventListener('keydown', function () { el._keyboard = true; });
    this.addEventListener('pointerdown', function (event) {
      el._keyboard = false;
      el._touch = event.pointerType === 'touch';
    });

    this.radios.forEach(function (radio) {
      var pick = function () { el.onRating(parseInt(radio.value, 10)); };
      radio.addEventListener('change', pick);
      radio.addEventListener('click', pick);
      radio.addEventListener('focus', function () { el.preview(parseInt(radio.value, 10)); });
      // Arrowing between ratings blurs one radio and focuses the next; resting
      // in between would flash the face back to neutral for a frame.
      radio.addEventListener('blur', function (event) {
        var to = event.relatedTarget;
        if (to && to.hasAttribute && to.hasAttribute('data-rating-input')) return;
        el.rest();
      });
    });

    /* Hover starts per option but ends only for the whole row. Releasing it per
       option meant every gap between two stars reset the face, so a slow sweep
       read as smile, flat, smile — or snapped back to the chosen rating between
       each one. Now the face travels straight from one rating to the next. */
    this.options.forEach(function (option) {
      option.addEventListener('mouseenter', function () { el.preview(parseInt(option.dataset.value, 10)); });
    });

    var group = this.querySelector('[data-rating-group]');
    if (group) group.addEventListener('mouseleave', function () { el.rest(); });

    this.querySelectorAll('[data-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var step = chip.closest('[data-step]');
        var pressed = chip.getAttribute('aria-pressed') === 'true';

        if (!pressed && step && step.dataset.select === 'single') {
          step.querySelectorAll('[data-chip]').forEach(function (other) {
            other.setAttribute('aria-pressed', 'false');
          });
        }

        chip.setAttribute('aria-pressed', pressed ? 'false' : 'true');
        if (step) el.hideError(step);
        el.saveProgress();
      });
    });

    this.querySelectorAll('[data-back]').forEach(function (btn) {
      btn.addEventListener('click', function () { el.goTo(el.pos - 1); });
    });

    this.querySelectorAll('[data-next]').forEach(function (btn) {
      btn.addEventListener('click', function () { el.next(false, btn); });
    });

    this.querySelectorAll('[data-skip]').forEach(function (btn) {
      btn.addEventListener('click', function () { el.next(true); });
    });

    // Bound directly as well as through the form's submit event, so the
    // button still works if a browser skips implicit form submission.
    this.querySelectorAll('[data-submit]').forEach(function (btn) {
      btn.addEventListener('click', function () { el.submit(); });
    });

    this.querySelectorAll('[data-email]').forEach(function (field) {
      field.addEventListener('input', function () {
        if (field.value.trim()) el.hideError(field.closest('[data-step]'));
      });
    });

    // unticking the box removes the reason the address was needed
    this.querySelectorAll('[data-marketing]').forEach(function (box) {
      box.addEventListener('change', function () {
        if (!box.checked) el.hideError(box.closest('[data-step]'));
      });
    });

    var restart = this.querySelector('[data-restart]');
    if (restart) restart.addEventListener('click', function () { el.reset(); });

    this.querySelectorAll('[data-feedback], [data-additional]').forEach(function (field) {
      field.addEventListener('input', function () {
        el.autoGrow(field);
        el.count(field);
        // an answer that now satisfies the rule should clear the complaint at
        // once, not wait for them to press the button again
        if (field.value.trim()) el.hideError(field.closest('[data-step]'));
        clearTimeout(el._saveTimer);
        el._saveTimer = setTimeout(function () { el.saveProgress(); }, 400);
      });
      field.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          el.next();
        }
      });
    });

    // Leaving inside the moment a tap waits should not lose that rating.
    window.addEventListener('pagehide', function () {
      if (el._capturePending) el.captureRating();
    });

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        el.submit();
      });
    }
  }

  observeViewport() {
    var el = this;
    if (!('IntersectionObserver' in window)) {
      this.classList.add('is-inview');
      return;
    }
    this._observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        el.classList.add('is-inview');
        el._observer.disconnect();
      });
    }, { threshold: 0.15 });
    this._observer.observe(this);
  }

  setupEditorPreview() {
    if (!window.Shopify || !window.Shopify.designMode) return;
    var target = this.dataset.editorStep;
    if (!target || target === 'auto') return;

    if (target === 'expired') {
      this.showExpired();
      return;
    }

    this.applyRating(this.scale);
    this.buildFlow(this.scale);

    var index = -1;
    this.flow.forEach(function (step, i) {
      if (step && step.dataset.step === target && index < 0) index = i;
    });
    if (index > 0) this.goTo(index, true);
  }

  /* A returning visitor with no link still picks up where they stopped. */
  resumeAnonymous() {
    if (!this.resumeEnabled || this.inviteEnabled) return;
    var key = 'rating-feedback:id:' + (this.id || 'section');
    try {
      this.responseId = window.localStorage.getItem(key) || null;
    } catch (error) {
      return;
    }
    if (this.responseId) this.restoreProgress();
  }

  /* ----------------------------------------------------- invite links */
  startInvite() {
    if (!this.inviteEnabled) return;

    /* Nothing about invite links runs in the theme editor: no token is checked,
       no expired screen takes over, and an invite-only page still shows the form
       so it can be styled. Use Preview a step to see the expired screen. */
    if (window.Shopify && window.Shopify.designMode) return;

    var el = this;
    var params = new URLSearchParams(window.location.search);
    var token = params.get('fb');
    var rating = parseInt(params.get('r'), 10);

    if (!token) {
      // No link at all: either an open feedback page, or a page that only
      // exists to serve invited customers.
      if (this.inviteRequired) this.showExpired();
      else if (this.inRange(rating)) this.openAt(rating);
      return;
    }

    this.classList.add('is-verifying');

    this.readInvite(token).then(function (invite) {
      el.classList.remove('is-verifying');
      if (!invite) {
        el.showExpired();
        return;
      }
      invite.reference = invite.reference || el.looseReference(params);
      el.applyInvite(invite);
      if (!el.restoreProgress()) {
        if (el.inRange(rating)) el.openAt(rating);
      }
    }).catch(function () {
      el.classList.remove('is-verifying');
      el.showExpired();
    });
  }

  /* payload.signature — verified with the shared secret, then checked for
     expiry. Returns null for anything that does not add up. */
  async readInvite(token) {
    var parts = String(token).split('.');
    if (parts.length !== 2 || !this.inviteSecret) return null;
    if (!window.crypto || !window.crypto.subtle) return null;

    var expected = await this.sign(parts[0]);
    if (expected !== parts[1]) return null;

    var payload;
    try {
      payload = JSON.parse(this.decodeBase64Url(parts[0]));
    } catch (error) {
      return null;
    }

    if (payload.x && Date.now() / 1000 > Number(payload.x)) return null;

    return {
      id: parts[1].slice(0, 16),
      email: payload.e || '',
      reference: payload.o || ''
    };
  }

  async sign(message, secret) {
    var encoder = new TextEncoder();
    var key = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(secret || this.inviteSecret),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    var signature = await window.crypto.subtle.sign('HMAC', key, encoder.encode(message));
    return this.encodeBase64Url(new Uint8Array(signature)).slice(0, 22);
  }

  encodeBase64Url(bytes) {
    var binary = '';
    for (var i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  decodeBase64Url(value) {
    var padded = value.replace(/-/g, '+').replace(/_/g, '/');
    while (padded.length % 4) padded += '=';
    var binary = window.atob(padded);
    var bytes = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  }

  /* Invited customers are identified by their link; everyone else gets an id
     minted the moment they choose a rating, so both can be resumed and
     de-duplicated the same way. */
  ensureResponseId() {
    if (this.responseId) return this.responseId;

    if (this.inviteId) {
      this.responseId = this.inviteId;
      return this.responseId;
    }

    var key = 'rating-feedback:id:' + (this.id || 'section');
    var stored = null;
    try {
      stored = window.localStorage.getItem(key);
    } catch (error) {
      stored = null;
    }

    if (!stored) {
      stored = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID().replace(/-/g, '').slice(0, 16)
        : Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      try {
        window.localStorage.setItem(key, stored);
      } catch (error) {
        /* private browsing — the id lives for this page view only */
      }
    }

    this.responseId = stored;
    return this.responseId;
  }

  /* Email platforms build a flow's URL at send time and cannot sign anything,
     so an order number may arrive beside the signed token rather than inside
     it. It is only ever a label, so an unsigned one is fine — but it is capped
     and stripped so it cannot be used to smuggle anything into the sheet. */
  /* The plain way to know who is answering: the email platform writes the
     address straight into the link, because it already has it.

       /pages/feedback?email={{ person.email }}

     Nothing is signed, so treat it as claimed rather than proven — anyone can
     edit their own address in the bar. For feedback that is a fair trade: it
     saves every honest customer from typing, and forging one gains nobody
     anything. Where it matters, use a signed invite link instead; if both are
     present the signed one wins, because startInvite runs after this. */
  readEmailParam() {
    if (!this.emailParam) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var field = this.querySelector('[data-email]');
    if (!field || field.value.trim()) return;

    var value = this.linkEmail(this.emailParam);
    if (!value) return;

    field.value = value;
    this.fromLink = true;

    if (this.hideKnownEmail) {
      var wrap = field.closest('.rating-feedback__field');
      if (wrap) wrap.hidden = true;
    }
  }

  /* The address in the link, or '' when there is none or it is not one. */
  linkEmail(param) {
    if (!param) return '';
    var value = new URLSearchParams(window.location.search).get(param);
    if (!value) return '';

    // A plus address sent unencoded (name+tag@gmail.com) arrives with a space
    // where the plus was. An email never holds a space, so put it back.
    value = value.trim().replace(/ /g, '+').slice(0, 254);
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) ? value : '';
  }

  looseReference(params) {
    var loose = params.get('ref');
    if (!loose) return '';
    return loose.replace(/[^\w \-#\/.]/g, '').trim().slice(0, 64);
  }

  applyInvite(invite) {
    this.inviteId = invite.id;
    this.responseId = invite.id;

    var reference = this.querySelector('[data-field="reference"]');
    if (reference) reference.value = invite.reference;
    this.inviteReference = invite.reference;

    var email = this.querySelector('[data-email]');
    if (email && invite.email) {
      email.value = invite.email;
      if (this.invitePrefill) {
        var field = email.closest('.rating-feedback__field');
        if (field) field.hidden = true;
      }
    }
  }

  /* Lands the customer on the question their emailed star already answered. */
  openAt(rating) {
    this.applyRating(rating);
    this.buildFlow(rating);
    // the star they tapped in the email is already a rating worth keeping
    this.captureRating();
    if (this.flow.length > 2) this.goTo(1, true);
  }

  showExpired() {
    if (!this.expiredStep) return;
    this.classList.add('is-compact');
    this.jumpTo(this.expiredStep);
    if (this.progress) this.progress.hidden = true;
  }

  jumpTo(step) {
    this.allSteps.forEach(function (item) { item.classList.remove('is-active'); });
    step.classList.add('is-active');
    this.viewport.style.height = '';
    var at = this.flow.indexOf(step);
    if (at > -1) this.pos = at;
  }

  /* ------------------------------------------------------ saved answers */
  /* Wipes the finished run so the next visit is a new one, with its own id and
     its own row rather than an edit of the last. */
  startFreshRun() {
    try {
      window.localStorage.removeItem(this.progressKey());
      window.localStorage.removeItem('rating-feedback:id:' + (this.id || 'section'));
      if (this.responseId) window.localStorage.removeItem(this.captureKey());
    } catch (error) {
      /* private browsing — there was nothing stored to clear */
    }

    this.responseId = this.inviteId || null;
    this._submitted = false;
  }

  retireResponseId() {
    try {
      window.localStorage.removeItem('rating-feedback:id:' + (this.id || 'section'));
      window.localStorage.removeItem(this.captureKey());
    } catch (error) {
      /* private browsing — nothing was stored */
    }
  }

  progressKey() {
    return 'rating-feedback:progress:' + (this.responseId || 'anon');
  }

  saveProgress(extra) {
    if (!this.resumeEnabled || !this.responseId) return;
    var el = this;

    var data = {
      rating: this.state(),
      pos: this.pos,
      answers: {},
      chips: {},
      additional: '',
      submitted: this._submitted === true,
      at: this._submitted === true ? Date.now() : (this._startedAt || Date.now())
    };

    this.questionSteps.forEach(function (step) {
      el.panelsOf(step).forEach(function (panel) {
        var key = panel.dataset.key;
        if (!key) return;
        var field = panel.querySelector('[data-feedback]');
        if (field && field.value) data.answers[key] = field.value;
        var chosen = toArray(panel.querySelectorAll('[data-chip][aria-pressed="true"]'))
          .map(function (chip) { return chip.textContent.trim(); });
        if (chosen.length) data.chips[key] = chosen;
      });
    });

    var additional = this.querySelector('[data-additional]');
    if (additional) data.additional = additional.value;

    Object.keys(extra || {}).forEach(function (key) { data[key] = extra[key]; });

    try {
      window.localStorage.setItem(this.progressKey(), JSON.stringify(data));
    } catch (error) {
      /* private browsing — the flow simply will not resume */
    }
  }

  restoreProgress() {
    // In the theme editor every reload should start clean, or the merchant sees
    // the thank-you from their last test instead of the form.
    if (window.Shopify && window.Shopify.designMode) return false;
    if (!this.resumeEnabled || !this.responseId) return false;

    var saved;
    try {
      saved = JSON.parse(window.localStorage.getItem(this.progressKey()));
    } catch (error) {
      return false;
    }
    if (!saved) return false;

    if (saved.submitted) {
      /* An invite is about one order, so once it is used it stays used. A public
         feedback page is different: the same customer may well have something
         new to say next month, so the thank-you only stands for as long as the
         repeat window, then they get a clean form and a new response id. */
      var spent = this.inviteId || (this.rateLimitMs && Date.now() - (saved.at || 0) < this.rateLimitMs);

      if (spent) {
        this.paintSuccess(saved.rating);
        this.jumpTo(this.successStep);
        if (this.progress) this.progress.hidden = true;
        this.classList.add('is-compact');
        return true;
      }

      this.startFreshRun();
      return false;
    }

    if (!isRating(saved.rating)) return false;

    this.applyRating(saved.rating);
    this.buildFlow(saved.rating);

    var el = this;
    this.questionSteps.forEach(function (step) {
      el.panelsOf(step).forEach(function (panel) {
        var key = panel.dataset.key;
        if (!key) return;

        var field = panel.querySelector('[data-feedback]');
        if (field && saved.answers && saved.answers[key]) {
          field.value = saved.answers[key];
          el.autoGrow(field);
          el.count(field);
        }

        var chosen = (saved.chips && saved.chips[key]) || [];
        panel.querySelectorAll('[data-chip]').forEach(function (chip) {
          if (chosen.indexOf(chip.textContent.trim()) > -1) chip.setAttribute('aria-pressed', 'true');
        });
      });
    });

    this.phones.forEach(function (phone) {
      var hidden = phone.querySelector('[data-feedback]');
      var input = phone.querySelector('[data-phone-input]');
      if (!hidden || !input || !hidden.value) return;

      var parts = hidden.value.split(' ');
      var dial = parts[0].replace('+', '');
      var match = phone.rfCountries.filter(function (country) { return country.dial === dial; })[0];
      if (match) el.setDial(phone, match);
      input.value = parts.slice(1).join('');
    });

    var additional = this.querySelector('[data-additional]');
    if (additional && saved.additional) {
      additional.value = saved.additional;
      this.autoGrow(additional);
      this.count(additional);
    }

    var target = Math.min(saved.pos || 1, this.flow.length - 2);
    if (target > 0) this.goTo(target, true);
    this.classList.toggle('is-compact', this.compactHeader && this.pos > 0);
    return true;
  }

  /* -------------------------------------------------------- rating ui */
  /* {n} {total} {word} — with any separator left dangling by a blank word
     trimmed off, so merchants can write the format they want. */
  fillTemplate(template, value, word) {
    return String(template || '{n}/{total} {word}')
      .replace(/\{n\}|\{rating\}/g, value)
      .replace(/\{total\}|\{scale\}/g, this.scale)
      .replace(/\{word\}|\{label\}/g, word || '')
      .replace(/[\s·—–\-|,]+$/, '')
      .trim();
  }

  /* fillTemplate trims dangling separators off short labels; a paragraph
     keeps every character the merchant wrote. */
  fillNote(text, value, word) {
    return String(text)
      .replace(/\{n\}|\{rating\}/g, value)
      .replace(/\{total\}|\{scale\}/g, this.scale)
      .replace(/\{word\}|\{label\}/g, word || '');
  }

  labelFor(rating) {
    if (!this.labels.length) return '';
    var i = Math.floor(((rating - this.min) / (this.scale - this.min + 1)) * this.labels.length);
    return this.labels[Math.min(this.labels.length - 1, Math.max(0, i))] || '';
  }

  state() {
    var checked = this.radios.filter(function (r) { return r.checked; })[0];
    return checked ? parseInt(checked.value, 10) : null;
  }

  inRange(rating) {
    return isRating(rating) && rating >= this.min && rating <= this.scale;
  }

  /* Stars fill from the first star up to the chosen one. A 0 is "no stars",
     so it fills none. */
  optionValue(option) {
    return parseInt(option.dataset.value, 10);
  }

  /* Hover, focus and touch all preview the rating: the face reacts and the
     word updates before anything is committed. */
  preview(rating, settling) {
    var hovering = isRating(rating);
    if (hovering) clearTimeout(this._rest);
    var current = this.state();
    var shown = hovering ? rating : current;
    var el = this;

    if (!this.classList.contains('rating-feedback--numbers')) {
      this.options.forEach(function (option) {
        var value = el.optionValue(option);
        option.classList.toggle('is-preview', hovering && value >= 1 && value <= shown);
      });
    }

    if (this.face) this.face.classList.toggle('is-previewing', hovering && rating !== current);

    this.paintFace(shown, settling ? 560 : 320);
    this.paintReaction(shown);
  }

  /* Back to the chosen rating, or to neutral — after a beat, and more slowly
     than a hover, so brushing past the edge of the row is not a jolt. Any new
     hover inside that beat cancels it. */
  rest() {
    var el = this;
    clearTimeout(this._rest);
    this._rest = setTimeout(function () { el.preview(null, true); }, REDUCED.matches ? 0 : 140);
  }

  paintFace(rating, duration) {
    if (!this.face) return;

    if (!isRating(rating)) {
      this.face.classList.remove('is-set', 'is-previewing');
      this.face.removeAttribute('style');
      this.setMood('');
      this.tweenMouth(this.restMouth, duration);
      return;
    }

    var t = this.scale > this.min ? (rating - this.min) / (this.scale - this.min) : 1;
    this.face.style.setProperty('--rf-eye-y', (2 - t * 3).toFixed(1) + 'px');
    this.face.style.setProperty('--rf-eye-scale', t > 0.85 ? '0.55' : '1');
    this.setMood(this.moodFor(rating));
    this.tweenMouth(this.npsMouth(rating), duration);
  }

  moodFor(rating) {
    if (rating >= this.happyFrom) return 'promoter';
    if (rating >= this.neutralFrom) return 'passive';
    return 'detractor';
  }

  /* Each mood owns a band of the mouth — sad frowns, neutral sits level,
     happy smiles — and the rating's place inside its band nudges it, so the
     smile still grows step by step instead of only at the boundaries. */
  npsMouth(rating) {
    var mood = this.moodFor(rating);
    var band = {
      detractor: [this.min, this.neutralFrom - 1, -1, -0.6],
      passive: [this.neutralFrom, this.happyFrom - 1, 0, 0.22],
      promoter: [this.happyFrom, this.scale, 0.72, 1]
    }[mood];

    var span = band[1] - band[0];
    var at = span > 0 ? (rating - band[0]) / span : (mood === 'promoter' ? 1 : 0);
    return band[2] + (band[3] - band[2]) * at;
  }

  /* The colour follows the mood through CSS; crossing into a new mood also
     gives the face a small bounce, so the change is felt as well as seen. */
  setMood(mood) {
    var was = this.face.dataset.mood || '';
    if (was === mood) return;

    if (mood) this.face.dataset.mood = mood;
    else delete this.face.dataset.mood;

    if (!mood || REDUCED.matches) return;
    this.face.classList.remove('is-bumping');
    void this.face.offsetWidth;
    this.face.classList.add('is-bumping');
  }

  /* -1 = frown, 0 = level, 1 = smile */
  mouthPath(value) {
    var y = 59 - value * 2.5;
    var depth = value * 15;
    return 'M33 ' + y.toFixed(1) + ' Q50 ' + (y + depth).toFixed(1) + ' 67 ' + y.toFixed(1);
  }

  tweenMouth(target, duration) {
    var el = this;
    var mouth = this.face.querySelector('.rating-feedback__face-mouth');
    if (!mouth) return;

    var from = typeof this._mouth === 'number' ? this._mouth : 0;
    var length = duration || 320;
    cancelAnimationFrame(this._mouthFrame);

    if (REDUCED.matches || from === target) {
      this._mouth = target;
      mouth.setAttribute('d', this.mouthPath(target));
      return;
    }

    var startedAt = performance.now();
    var step = function (now) {
      var progress = Math.min(1, (now - startedAt) / length);
      var eased = 1 - Math.pow(1 - progress, 3);
      el._mouth = from + (target - from) * eased;
      mouth.setAttribute('d', el.mouthPath(el._mouth));
      if (progress < 1) el._mouthFrame = requestAnimationFrame(step);
    };

    this._mouthFrame = requestAnimationFrame(step);
  }

  paintReaction(rating) {
    if (!this.reaction) return;
    var word = isRating(rating) ? this.labelFor(rating) : '';
    if (this.reaction.dataset.word === word) return;
    this.reaction.dataset.word = word;
    this.reaction.innerHTML = word ? '<span>' + word + '</span>' : '';
  }

  applyRating(rating) {
    var radio = this.radios.filter(function (r) { return parseInt(r.value, 10) === rating; })[0];
    if (radio) radio.checked = true;
    this.paint(rating);
  }

  paint(rating) {
    var el = this;
    this.options.forEach(function (option) {
      var value = el.optionValue(option);
      option.classList.toggle('is-filled', value >= 1 && value <= rating);
      option.classList.remove('is-preview');
    });

    if (this.face) {
      this.face.classList.add('is-set');
      this.face.classList.remove('is-previewing');
    }

    this.paintFace(rating);
    this.paintReaction(rating);
  }

  onRating(rating) {
    if (!isRating(rating)) return;
    this.ensureResponseId();
    this.paint(rating);
    if (this.pos === 0 && this.started) this.refreshRatingStep();
    this.hideError(this.ratingStep);
    this.queueCapture();

    if (this.pos !== 0 || !this.autoAdvance) return;

    var el = this;
    var delay = REDUCED.matches ? 0 : this.advanceDelay;
    // Touch has no hover, so the face only reacts on tap — hold the step a
    // beat longer so that animation is actually seen before moving on.
    if (this._touch) delay = Math.max(delay, this.touchDelay);
    if (this._keyboard) delay = Math.max(delay, 900);

    clearTimeout(this._advance);
    this._advance = setTimeout(function () { el.next(); }, delay);
  }

  /* ------------------------------------- flow built from theme blocks */
  buildFlow(rating) {
    var el = this;
    var matches = this.questionSteps.filter(function (step) {
      return rating >= step.rfMin && rating <= step.rfMax;
    });

    var merged = this.mergeClosing && matches.length > 0;
    if (merged) this.absorbExtra(matches[matches.length - 1]);
    else this.releaseExtra();

    this.flow = [this.ratingStep].concat(matches);
    if (this.extraStep && !merged) this.flow.push(this.extraStep);
    this.flow.push(this.successStep);
    this.started = true;

    var word = this.labelFor(rating);

    this.questionSteps.forEach(function (step) {
      var on = matches.indexOf(step) >= 0;
      step.querySelectorAll('[data-feedback], [data-phone-input]').forEach(function (field) { field.disabled = !on; });

      var lead = on && matches[0] === step;

      var summary = step.querySelector('[data-summary]');
      if (summary) {
        var first = lead && el.showSummary;
        summary.hidden = !first;
        if (first) {
          // "Your rating: [9]" — a label and the number alone. A bare "9" means
          // little read aloud, so screen readers get the full wording instead.
          var big = el.dataset.summaryStyle !== 'line';
          var value = summary.querySelector('[data-summary-text]');
          var label = summary.querySelector('[data-summary-label]');
          var spoken = summary.querySelector('[data-summary-sr]');

          value.textContent = big ? String(rating) : el.fillTemplate(el.dataset.summaryTemplate, rating, word);
          if (big) value.setAttribute('aria-hidden', 'true');
          else value.removeAttribute('aria-hidden');
          if (label) label.textContent = big ? (el.dataset.summaryLabel || '') : '';
          if (spoken) spoken.textContent = big ? ' ' + el.fillTemplate(el.dataset.ratingLabelTemplate, rating, word) : '';
        }
      }

      // A few lines under the rating. Its own branch can say something different
      // to a 2 than to a 9; otherwise the section's message is used.
      var note = step.querySelector('[data-summary-note]');
      if (note) {
        var branch = step.closest('[data-branch]');
        var text = lead ? ((branch && branch.dataset.note) || el.dataset.summaryNote || '').trim() : '';
        note.hidden = !text;
        note.textContent = text ? el.fillNote(text, rating, word) : '';
      }

      // the panel only exists while it has something in it
      var receipt = step.querySelector('[data-receipt]');
      if (receipt) receipt.hidden = !((summary && !summary.hidden) || (note && !note.hidden));
    });

    this.labelButtons();
  }

  labelButtons() {
    var el = this;
    var last = this.flow.length - 2;
    this.flow.forEach(function (step, i) {
      if (!step || step === el.successStep) return;
      var label = step.querySelector('[data-next] [data-label], [data-submit] [data-label]');
      if (!label) return;

      // Returning to a rating you already chose is a different act from picking
      // one, and the button should say so.
      if (step === el.ratingStep && isRating(el.state()) && el.started) {
        label.textContent = el.dataset.continueLabel || el.dataset.nextLabel;
        return;
      }

      label.textContent = i === last ? el.dataset.submitLabel : el.dataset.nextLabel;
    });
  }

  /* Coming back to the rating step, the customer has no way of knowing that
     tapping the same star again moves them on. So it stops being the only way. */
  refreshRatingStep() {
    if (!this.ratingStep) return;

    var chosen = isRating(this.state()) && this.started;
    var actions = this.ratingStep.querySelector('[data-rating-actions]');
    var hint = this.ratingStep.querySelector('[data-rating-hint]');

    if (actions && this.autoAdvance) {
      var wasHidden = actions.hidden;
      actions.hidden = !chosen;
      if (wasHidden && chosen) {
        actions.classList.remove('is-revealed');
        void actions.offsetWidth;
        actions.classList.add('is-revealed');
      }
    }

    if (hint) {
      hint.textContent = chosen
        ? (this.dataset.hintReturn || this.dataset.hintFirst || '')
        : (this.dataset.hintFirst || '');
    }

    this.labelButtons();
  }

  /* -------------------------------------------------- step transitions */
  goTo(nextPos, silent) {
    if (nextPos < 0 || nextPos >= this.flow.length || nextPos === this.pos) return;
    if (this.moving) return;

    var el = this;
    var current = this.flow[this.pos];
    var next = this.flow[nextPos];
    if (!current || !next) return;

    var dir = nextPos > this.pos ? 1 : -1;

    current.style.setProperty('--rf-enter-y', dir > 0 ? '-14px' : '14px');
    next.style.setProperty('--rf-enter-y', dir > 0 ? '18px' : '-18px');

    this.viewport.style.height = this.viewport.offsetHeight + 'px';
    this.viewport.classList.add('is-animating');
    void this.viewport.offsetHeight;

    // before measuring, since showing the rating changes the card's height
    if (next === this.successStep) this.paintSuccess(this.state());

    var targetHeight = next.offsetHeight;

    current.classList.remove('is-active');
    next.classList.add('is-active');
    this.pos = nextPos;

    requestAnimationFrame(function () {
      el.viewport.style.height = targetHeight + 'px';
    });

    this.moving = true;
    clearTimeout(this._settle);
    this._settle = setTimeout(function () {
      el.moving = false;
      if (el.queued) {
        el.queued = false;
        el.next();
      }
      el.viewport.classList.remove('is-animating');
      el.viewport.style.height = '';
      next.style.removeProperty('--rf-enter-y');
      current.style.removeProperty('--rf-enter-y');
    }, REDUCED.matches ? 40 : 640);

    this.updateProgress();
    if (next === this.ratingStep) this.refreshRatingStep();
    if (this.compactHeader) this.classList.toggle('is-compact', nextPos > 0);
    this.saveProgress();
    if (!silent) this.focusStep(next);
  }

  focusStep(step) {
    var el = this;

    setTimeout(function () {
      if (step === el.successStep) {
        step.focus({ preventScroll: true });
        var announce = step.querySelector('[data-success-announce]');
        if (announce) announce.textContent = el.dataset.successAnnounce || '';
        el.burst();
        return;
      }

      if (!window.matchMedia('(hover: hover)').matches) return;
      var field = step.querySelector('[data-phone-input]:not([disabled]), [data-feedback]:not([disabled]), [data-additional]');
      if (field) field.focus({ preventScroll: true });
    }, REDUCED.matches ? 0 : 260);

    var rect = this.getBoundingClientRect();
    if (rect.top < 0) {
      window.scrollTo({
        top: rect.top + window.pageYOffset - 24,
        behavior: REDUCED.matches ? 'auto' : 'smooth'
      });
    }
  }

  updateProgress() {
    var done = this.flow[this.pos] === this.successStep;
    var total = this.started ? this.flow.length - 1 : this.stepTotal;
    var current = Math.min(this.pos + 1, total);
    var percent = done ? 100 : (current / total) * 100;

    if (this.bar) this.bar.style.setProperty('--rf-progress', percent + '%');
    // stills the water once there is nowhere left to go
    if (this.progress) this.progress.classList.toggle('is-complete', percent >= 100);

    if (this.stepCount) {
      var template = this.dataset.progressTemplate || 'Step {step} of {total}';
      this.stepCount.textContent = done ? '' : template.replace('{step}', current).replace('{total}', total);
    }

    if (this.progress) this.progress.hidden = done;
  }

  /* -------------------------------------------------------- validation */
  /* The message belongs to the step, but the highlight belongs to the block
     that actually failed — on a one-screen flow, reddening every field would
     say nothing about which one needs attention. */
  /* Each block owns the line beneath it. A merged page also owns one of its own,
     next to the button, for things that are about the whole submission. */
  errorBoxOf(target) {
    if (!target) return null;
    return target.querySelector(':scope > [data-page-error]') ||
      target.querySelector(':scope > [data-error]') ||
      target.querySelector('[data-error]');
  }

  showError(target, message) {
    var box = this.errorBoxOf(target);
    if (box) {
      if (message) box.textContent = message;
      box.classList.add('is-visible');
    }

    target.classList.add('is-invalid');
    setTimeout(function () { target.classList.remove('is-invalid'); }, 600);
  }

  hideError(step) {
    if (!step) return;
    var boxes = toArray(step.querySelectorAll('[data-error]'));
    var own = this.errorBoxOf(step);
    if (own && boxes.indexOf(own) < 0) boxes.push(own);
    boxes.forEach(function (box) { box.classList.remove('is-visible', 'is-neutral'); });
  }

  next(skipValidation, source) {
    var step = this.flow[this.pos];
    if (!step || this.busy) return;

    /* A tap mid-transition means one of two things. If it came from the button
       on the step now arriving, the customer is ahead of the animation and it is
       queued. If it came from the button on the step leaving, they are just
       tapping impatiently — and skipping a whole question would be wrong. */
    if (this.moving) {
      if (source && source.closest('[data-step]') === step) this.queued = true;
      return;
    }

    if (step === this.ratingStep) {
      var rating = this.state();
      if (!isRating(rating)) {
        this.showError(step);
        return;
      }
      this.buildFlow(rating);
      // moving on settles the choice, so there is no reason to keep waiting
      this.captureRating();
    } else if (!skipValidation && !this.checkStep(step)) {
      return;
    }

    this.hideError(step);

    if (this.pos >= this.flow.length - 2) {
      this.submit();
      return;
    }

    this.goTo(this.pos + 1);
  }

  /* Every rule carried by the blocks on this screen. Called from Next and from
     Submit alike — on a one-screen flow the closing button is the only button,
     and it must not be a way around them. */
  checkStep(step) {
    if (!step || step.dataset.step !== 'question') return true;

    var el = this;
    this.hideError(step);

    var failures = [];

    this.panelsOf(step).forEach(function (panel) {
      var field = panel.querySelector('[data-feedback]');
      var phoneInput = panel.querySelector('[data-phone-input]');
      var needed = parseInt(panel.dataset.minChips, 10) || 0;

      if (needed > 0 && panel.querySelectorAll('[data-chip][aria-pressed="true"]').length < needed) {
        failures.push({ panel: panel, message: (el.dataset.errorChips || '').replace('{n}', needed) });
        return;
      }

      if (panel.dataset.required === 'true' && field && !field.value.trim()) {
        // asking for a phone number and asking for a sentence are not the same ask
        failures.push({
          panel: panel,
          message: phoneInput ? el.dataset.errorPhone : el.dataset.errorRequired,
          focus: phoneInput || field
        });
        return;
      }

      if (phoneInput && phoneInput.value.trim()) {
        var typed = phoneInput.value.replace(/\D/g, '');
        if (typed.length < 6 || typed.length > 14) {
          failures.push({ panel: panel, message: el.dataset.errorPhone, focus: phoneInput });
        }
      }
    });

    // show them all, so nobody has to discover their mistakes one at a time
    failures.forEach(function (failure) { el.showError(failure.panel, failure.message); });
    if (failures.length && failures[0].focus) failures[0].focus.focus();

    return failures.length === 0;
  }

  /* ------------------------------------------------------------ fields */
  autoGrow(field) {
    if (field.tagName !== 'TEXTAREA') return;
    field.style.height = 'auto';
    field.style.height = field.scrollHeight + 'px';
  }

  count(field) {
    var wrap = field.closest('.rating-feedback__field');
    var counter = wrap ? wrap.querySelector('[data-counter]') : null;
    if (!counter) return;

    var max = parseInt(field.getAttribute('maxlength'), 10) || 0;
    var used = field.value.length;
    counter.textContent = used + ' / ' + max;
    counter.classList.toggle('is-near', max > 0 && used > max * 0.85);
    // 'near' keeps the count out of the way until it starts to matter
    counter.classList.toggle('is-shown', this.counterDisplay === 'always' || (max > 0 && used > max * 0.6));
  }

  collect() {
    var rating = this.state();
    var extra = this.querySelector('[data-additional]');
    var email = this.querySelector('[data-email]');
    var phoneField = this.querySelector('[data-phone] [data-feedback]');
    var marketing = this.querySelector('[data-marketing]');
    var tags = [];

    var el = this;
    var responses = this.flow
      .filter(function (step) { return step && step.dataset.step === 'question'; })
      .reduce(function (all, step) { return all.concat(el.panelsOf(step)); }, [])
      .filter(function (panel) {
        if (panel.hasAttribute('data-additional-panel')) return false;
        // a Message block says something rather than asking something, so it is
        // not a question and does not belong in the answers
        return panel.querySelector('[data-feedback]') || panel.querySelector('[data-chip]');
      })
      .map(function (panel) {
        var prompt = panel.querySelector('.rating-feedback__prompt');
        var field = panel.querySelector('[data-feedback]');
        var chips = toArray(panel.querySelectorAll('[data-chip][aria-pressed="true"]')).map(function (chip) {
          return chip.textContent.trim();
        });
        tags = tags.concat(chips);
        return {
          question: prompt ? prompt.textContent.trim() : '',
          answer: field ? field.value.trim() : '',
          tags: chips
        };
      });

    var readable = responses.map(function (item) {
      var lines = [];
      if (item.question) lines.push('Q: ' + item.question);
      if (item.answer) lines.push('A: ' + item.answer);
      if (item.tags.length) lines.push('Chose: ' + item.tags.join(', '));
      return lines.join('\n');
    });

    /* The closing question is not one of the blocks, so it has to be added to
       both records by hand — and to BOTH, or the readable column and the data
       column disagree and FEEDBACK_ANSWER cannot find it. */
    var structured = responses.slice();

    if (extra && extra.value.trim()) {
      var extraPrompt = this.extraStep ? this.extraStep.querySelector('.rating-feedback__prompt') : null;
      var extraTitle = extraPrompt ? extraPrompt.textContent.trim() : 'Anything else';

      readable.push('Q: ' + extraTitle + '\nA: ' + extra.value.trim());
      structured.push({ question: extraTitle, answer: extra.value.trim(), tags: [] });
    }

    return {
      response_id: this.responseId || '',
      rating: rating,
      rating_label: this.labelFor(rating),
      rating_scale: this.scale,
      // the first answer someone actually wrote, not the first block that happens
      // to be a choices step with nothing typed in it
      rating_feedback: (responses.filter(function (item) { return item.answer; })[0] || {}).answer || '',
      feedback_tags: tags,
      responses: responses,
      answers_text: readable.join('\n\n'),
      answers_json: JSON.stringify(structured),
      additional_feedback: extra ? extra.value.trim() : '',
      email: email ? email.value.trim() : '',
      marketing: marketing ? marketing.checked : false,
      phone: phoneField ? phoneField.value.trim() : '',
      reference: this.inviteReference || '',
      invite_id: this.inviteId || '',
      customer_id: this.customerId,
      market: this.market,
      page: window.location.pathname,
      submitted_at: new Date().toISOString()
    };
  }

  fill(payload) {
    var el = this;
    var ratingText = payload.rating + '/' + payload.rating_scale +
      (payload.rating_label ? ' (' + payload.rating_label + ')' : '');
    var answers = payload.responses.map(function (item) { return item.answer; }).filter(Boolean);

    var map = {
      response_id: payload.response_id,
      phone: payload.phone,
      marketing: payload.marketing ? 'yes' : 'no',
      market: payload.market,
      reference: payload.reference,
      rating: ratingText,
      rating_feedback: answers.join(' | '),
      tags: payload.feedback_tags.join(', '),
      additional_feedback: payload.additional_feedback
    };

    Object.keys(map).forEach(function (key) {
      var input = el.querySelector('[data-field="' + key + '"]');
      if (input) input.value = map[key];
    });

    var body = ['Rating: ' + ratingText];
    payload.responses.forEach(function (item) {
      body.push('');
      if (item.question) body.push(item.question);
      if (item.answer) body.push(item.answer);
      if (item.tags.length) body.push('Selected: ' + item.tags.join(', '));
    });
    if (payload.additional_feedback) {
      body.push('');
      body.push('Anything else: ' + payload.additional_feedback);
    }
    body.push('');
    body.push('Page: ' + payload.page);

    var bodyInput = this.querySelector('[data-field="body"]');
    if (bodyInput) bodyInput.value = body.join('\n');
  }

  /* ------------------------------------------------------------ submit */
  async submit() {
    if (this.busy) return;

    var step = this.flow[this.pos];
    if (!this.checkStep(step)) return;

    var payload = this.collect();

    var emailField = this.querySelector('[data-email]');
    var wantsEmail = this.querySelector('[data-marketing]');

    /* Ticking the marketing box is a promise we cannot keep without an address:
       the signup silently does nothing, and the row records a yes we can never
       act on. So the box makes the field required, whatever the setting says. */
    if (emailField && wantsEmail && wantsEmail.checked && !emailField.value.trim()) {
      this.showError(
        emailField.closest('[data-panel]') || step,
        this.dataset.errorMarketingEmail || this.dataset.errorEmail
      );
      emailField.focus();
      return;
    }

    if (emailField && (this.emailRequired || emailField.value.trim())) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailField.value.trim())) {
        this.showError(emailField.closest('[data-panel]') || step, this.dataset.errorEmail);
        emailField.focus();
        return;
      }
    }

    var blocked = this.spamCheck(step);
    if (blocked === 'silent') {
      this.goTo(this.flow.length - 1);
      return;
    }
    if (blocked) return;

    // Claimed before the first await: the button click and the form's own submit
    // event both land here, and only one of them may get through.
    var button = step.querySelector('[data-submit], [data-next]');
    this.busy = true;

    if (this.verifySecret) {
      try {
        payload.signature = await this.sign(
          [payload.response_id, payload.rating, payload.submitted_at, payload.page].join('|'),
          this.verifySecret
        );
      } catch (error) {
        payload.signature = '';
      }
    }

    this.fill(payload);
    this.hideError(step);

    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    }

    var ok = true;
    try {
      ok = await this.send(payload);
    } catch (error) {
      ok = false;
    }

    this.busy = false;
    if (button) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }

    if (!ok) {
      this.showError(step, this.dataset.errorSubmit);
      return;
    }

    this.signUp(payload);
    this.writeStamp();
    this._submitted = true;
    this.saveProgress();

    // With resuming off nothing else retires this id, so the next visit on
    // this browser would reuse it — and its rating rows would pair up with
    // this finished response in the sheet. The next visit starts its own.
    if (!this.resumeEnabled) this.retireResponseId();

    this.dispatchEvent(new CustomEvent('rating-feedback:submitted', {
      bubbles: true,
      composed: true,
      detail: payload
    }));

    this.goTo(this.flow.length - 1);
  }

  /* Returns 'silent' to swallow a bot submission, true to block with a
     message, false to carry on. */
  spamCheck(step) {
    // The merchant testing their own form is not spam.
    if (window.Shopify && window.Shopify.designMode) return false;
    if (!this.spamProtection) return false;

    var honeypot = this.querySelector('[data-honeypot]');
    if (honeypot && honeypot.value.trim()) return 'silent';

    if (this.minSubmitMs && Date.now() - this.mountedAt < this.minSubmitMs) {
      this.showError(step, this.dataset.errorSubmit);
      return true;
    }

    if (this.rateLimitMs) {
      var last = this.readStamp();
      if (last && Date.now() - last < this.rateLimitMs) {
        var box = step.querySelector('[data-error]');
        if (box) box.classList.add('is-neutral');
        this.showError(step, this.dataset.errorDuplicate);
        return true;
      }
    }

    return false;
  }

  readStamp() {
    try {
      return parseInt(window.localStorage.getItem(this.storageKey), 10) || 0;
    } catch (error) {
      return 0;
    }
  }

  writeStamp() {
    try {
      window.localStorage.setItem(this.storageKey, String(Date.now()));
    } catch (error) {
      /* private browsing — rate limiting simply does not apply */
    }
  }

  /* Wraps fetch so a dead connection fails in seconds with a retry, instead of
     leaving the button spinning until the customer gives up. */
  async reach(url, options) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, this.timeout) : null;

    try {
      return await fetch(url, controller ? Object.assign({ signal: controller.signal }, options) : options);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  async send(payload) {
    if (window.Shopify && window.Shopify.designMode) return true;

    if (this.method === 'endpoint' && this.endpoint) {
      // 'simple' keeps the request CORS-preflight free, which is what
      // Google Apps Script and similar no-server endpoints need.
      var contentType = this.endpointMode === 'simple'
        ? 'text/plain;charset=utf-8'
        : 'application/json';

      var response = await this.reach(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': contentType },
        body: JSON.stringify(payload)
      });

      if (response.ok && this.alsoEmail) {
        this.sendContact().catch(function () {});
      }

      return response.ok;
    }

    if (this.method === 'google_form') {
      if (this.googleForm) {
        var stored = await this.sendGoogleForm(payload);
        if (stored && this.alsoEmail) this.sendContact().catch(function () {});
        return stored;
      }

      // No usable form URL — a wrong link should cost you a tidy row, never the
      // response itself, so it goes to the inbox instead.
      return this.sendContact();
    }

    if (this.method === 'cart') {
      var saved = await this.sendCartAttributes(payload);
      if (saved && this.alsoEmail) this.sendContact().catch(function () {});
      return saved;
    }

    if (this.method === 'contact') return this.sendContact();

    return true;
  }

  /* "rating=entry.123, email=entry.456" → { rating: 'entry.123', … } */
  parseEntryMap(raw) {
    var map = {};
    (raw || '').split(/[\n,]+/).forEach(function (pair) {
      var parts = pair.split('=');
      if (parts.length !== 2) return;
      var key = parts[0].trim();
      var field = parts[1].trim();
      if (key && field) map[key] = field;
    });
    return map;
  }

  /* A spreadsheet link pasted into the form URL by mistake would both fail to
     work and publish a private document's address in the page source. Refuse it. */
  safeFormUrl(url) {
    var value = String(url || '').trim();
    if (!value) return '';

    if (value.indexOf('/spreadsheets/') > -1) {
      if (window.console && window.console.warn) {
        window.console.warn('[rating-feedback] That is a Google Sheet link, not a Google Form response URL. Ignoring it so the sheet address is not published on the page.');
      }
      return '';
    }

    return value;
  }

  /* Reads a pre-filled Google Form link. Whatever was typed into each box is
     taken as the key for that field, so filling the boxes with the key names is
     all it takes to produce a complete map. */
  mapFromPrefill(link) {
    var known = PREFILL_KEYS;

    var map = {};
    var query = String(link || '').split('?')[1];
    if (!query) return map;

    query.split('&').forEach(function (pair) {
      var at = pair.indexOf('=');
      if (at < 0) return;

      var field = pair.slice(0, at).trim();
      if (field.indexOf('entry.') !== 0) return;

      var key = decodeURIComponent(pair.slice(at + 1).replace(/\+/g, ' '))
        .trim().toLowerCase().replace(/[\s-]+/g, '_');

      if (known.indexOf(key) > -1) map[key] = field;
    });

    return map;
  }

  /* "entry.123=vilvah-2026" lines → [['entry.123', 'vilvah-2026'], …] */
  parseStaticFields(raw) {
    var list = [];
    (raw || '').split(/[\n;]+/).forEach(function (line) {
      var at = line.indexOf('=');
      if (at < 1) return;
      var field = line.slice(0, at).trim();
      var value = line.slice(at + 1).trim();
      if (field && value) list.push([field, value]);
    });
    return list;
  }

  /* Google Form → the linked Google Sheet. No backend, no app: the POST is
     sent opaque (no-cors), so a network error is the only failure signal. */
  async sendGoogleForm(payload) {
    // A second form keeps a market's responses in their own sheet tab. With
    // none set, everything lands in one sheet and the market column splits it.
    var separate = this.market === 'global' && this.googleFormGlobal;
    var endpoint = separate ? this.googleFormGlobal : this.googleForm;
    var map = separate && Object.keys(this.googleMapGlobal).length
      ? this.googleMapGlobal
      : this.googleMap;

    var data = new FormData();
    var values = {
      rating: payload.rating,
      rating_label: payload.rating_label,
      rating_scale: payload.rating_scale,
      rating_feedback: payload.rating_feedback,
      feedback_tags: payload.feedback_tags.join(', '),
      additional_feedback: payload.additional_feedback,
      email: payload.email,
      phone: payload.phone,
      marketing: payload.marketing ? 'yes' : 'no',
      reference: payload.reference,
      response_id: payload.response_id,
      customer_id: payload.customer_id,
      invite_id: payload.invite_id,
      answers_text: payload.answers_text,
      answers_json: payload.answers_json,
      market: payload.market,
      signature: payload.signature || '',
      page: payload.page,
      submitted_at: payload.submitted_at,
      body: (this.querySelector('[data-field="body"]') || {}).value || ''
    };

    Object.keys(map).forEach(function (key) {
      if (values[key] === undefined || values[key] === '') return;
      data.append(map[key], values[key]);
    });

    // Constant fields, e.g. a required token that Google itself validates,
    // so posts that do not carry it never reach the sheet.
    this.googleStatic.forEach(function (pair) {
      data.append(pair[0], pair[1]);
    });

    if (!Array.from(data.keys()).length) return false;

    await this.reach(endpoint, { method: 'POST', mode: 'no-cors', body: data });
    return true;
  }

  /* ------------------------------------------------- the early rating */
  /* A second, smaller Google Form that takes the rating the moment it is
     chosen, so a customer who stops right there is still counted. Its rows land
     on their own tab of the same spreadsheet. Everything comes from its
     pre-filled link: where to post, which box is which, and the token — the one
     box whose value is not a field name. */
  captureTarget(link) {
    var value = String(link || '').trim();
    var base = value.split('?')[0];
    if (!/\/viewform$/.test(base)) return null;

    var url = this.safeFormUrl(base.replace(/\/viewform$/, '/formResponse'));
    var map = this.mapFromPrefill(value);
    if (!url || !map.rating) return null;

    var constants = [];
    (value.split('?')[1] || '').split('&').forEach(function (pair) {
      var at = pair.indexOf('=');
      var field = pair.slice(0, at).trim();
      if (at < 0 || field.indexOf('entry.') !== 0) return;

      var raw = decodeURIComponent(pair.slice(at + 1).replace(/\+/g, ' ')).trim();
      if (raw && PREFILL_KEYS.indexOf(raw.toLowerCase().replace(/[\s-]+/g, '_')) < 0) constants.push([field, raw]);
    });

    return { url: url, map: map, constants: constants };
  }

  captureKey() {
    return 'rating-feedback:captured:' + (this.responseId || 'anon');
  }

  /* A tap waits a moment, so someone trying 3, then 4, then 5 leaves one row
     rather than three. Moving on sends it at once. */
  queueCapture() {
    if (!this.capture) return;
    var el = this;
    clearTimeout(this._captureTimer);
    this._capturePending = true;
    this._captureTimer = setTimeout(function () { el.captureRating(); }, 1500);
  }

  async captureRating() {
    clearTimeout(this._captureTimer);
    this._capturePending = false;
    if (!this.capture) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var honeypot = this.querySelector('[data-honeypot]');
    if (honeypot && honeypot.value.trim()) return;

    var rating = this.state();
    if (!isRating(rating)) return;
    this.ensureResponseId();

    // The email field may sit on a later step, or not exist at all, so the
    // address in the link is read directly when the field has nothing yet.
    var field = this.querySelector('[data-email]');
    var email = (field && field.value.trim()) || this.linkEmail(this.emailParam || 'email');

    // One row per rating they settle on. Coming back to change it adds a row
    // under the same response id; landing on the same rating again does not —
    // unless this visit knows who they are and the earlier one did not. A
    // first visit straight to the page and a second from the email link must
    // not collapse into the row that has no address.
    var key = this.captureKey();
    var stamp = rating + '|' + email;
    try {
      if (window.localStorage.getItem(key) === stamp) return;
      window.localStorage.setItem(key, stamp);
    } catch (error) {
      if (this._captured === stamp) return;
    }
    this._captured = stamp;

    var values = {
      response_id: this.responseId,
      rating: rating,
      rating_label: this.labelFor(rating),
      rating_scale: this.scale,
      email: email,
      reference: this.inviteReference || '',
      invite_id: this.inviteId || '',
      customer_id: this.customerId,
      market: this.market,
      page: window.location.pathname,
      submitted_at: new Date().toISOString()
    };

    if (this.verifySecret) {
      try {
        values.signature = await this.sign(
          [values.response_id, values.rating, values.submitted_at, values.page].join('|'),
          this.verifySecret
        );
      } catch (error) {
        values.signature = '';
      }
    }

    var data = new FormData();
    var map = this.capture.map;
    Object.keys(map).forEach(function (name) {
      if (values[name] === undefined || values[name] === '') return;
      data.append(map[name], values[name]);
    });
    this.capture.constants.forEach(function (pair) { data.append(pair[0], pair[1]); });

    this.dispatchEvent(new CustomEvent('rating-feedback:rated', {
      bubbles: true,
      composed: true,
      detail: values
    }));

    try {
      // keepalive lets it finish even if they close the tab right after tapping
      await this.reach(this.capture.url, { method: 'POST', mode: 'no-cors', body: data, keepalive: true });
    } catch (error) {
      // a dropped connection should not cost the row: let the next chance retry
      this._captured = '';
      try {
        window.localStorage.removeItem(key);
      } catch (ignore) {
        /* private browsing */
      }
    }
  }

  /* Cart attributes ride along to the order, where they show up in the
     Shopify admin under the order's Additional details. */
  async sendCartAttributes(payload) {
    var prefix = this.cartPrefix ? this.cartPrefix + ' ' : '';
    var attributes = {};
    attributes[prefix + 'rating'] = payload.rating + '/' + payload.rating_scale +
      (payload.rating_label ? ' (' + payload.rating_label + ')' : '');
    attributes[prefix + 'comment'] = payload.rating_feedback;
    attributes[prefix + 'highlights'] = payload.feedback_tags.join(', ');
    attributes[prefix + 'additional'] = payload.additional_feedback;

    var response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    });

    return response.ok;
  }

  /* Shopify's own customer form: creates them with marketing consent, or does
     nothing at all if that address is already a customer. Either way the
     feedback submission does not wait on it. */
  signUp(payload) {
    if (!payload.marketing || !payload.email) return;

    var form = this.querySelector('.rating-feedback__signup');
    if (!form) return;

    var field = form.querySelector('[data-signup-email]');
    if (field) field.value = payload.email;

    this.reach(form.getAttribute('action') || '/contact', {
      method: 'POST',
      headers: { Accept: 'text/html' },
      body: new FormData(form)
    }).catch(function () {
      /* the response itself is already stored; the signup is a bonus */
    });
  }

  async sendContact() {
    if (!this.form) return true;

    var data = new FormData(this.form);
    if (!String(data.get('contact[email]') || '').trim()) {
      data.set('contact[email]', this.dataset.fallbackEmail || '');
    }

    var contact = await this.reach(this.form.getAttribute('action') || '/contact', {
      method: 'POST',
      headers: { Accept: 'text/html' },
      body: data
    });

    return contact.ok && !/contact_posted=false/.test(contact.url);
  }

  /* ----------------------------------------------------------- success */
  /* "You rated: 7" on the thank-you card. Hidden when the rating is unknown,
     rather than showing an empty chip. */
  paintSuccess(rating) {
    var row = this.querySelector('[data-success-rated]');
    if (!row) return;

    row.hidden = !isRating(rating);
    var value = row.querySelector('[data-success-rating]');
    if (isRating(rating) && value) {
      value.textContent = this.fillTemplate(this.dataset.successRatingTemplate || '{rating}', rating, this.labelFor(rating));
    }
  }

  burst() {
    var host = this.querySelector('[data-burst]');
    if (!host || REDUCED.matches || host.childElementCount) return;

    var palette = ['var(--rf-accent)', 'var(--rf-star)', 'var(--rf-accent)'];
    for (var i = 0; i < 12; i++) {
      var particle = document.createElement('span');
      var angle = (Math.PI * 2 * i) / 12 + Math.random() * 0.4;
      var distance = 58 + Math.random() * 44;
      particle.className = 'rating-feedback__particle';
      particle.style.setProperty('--rf-p-x', Math.cos(angle) * distance + 'px');
      particle.style.setProperty('--rf-p-y', Math.sin(angle) * distance + 'px');
      particle.style.setProperty('--rf-p-r', Math.round(Math.random() * 360) + 'deg');
      particle.style.setProperty('--rf-p-delay', Math.round(Math.random() * 160) + 'ms');
      particle.style.setProperty('--rf-p-color', palette[i % palette.length]);
      host.appendChild(particle);
    }
  }

  reset() {
    var el = this;

    this.startFreshRun();
    this.radios.forEach(function (radio) { radio.checked = false; });
    this.options.forEach(function (option) { option.classList.remove('is-filled', 'is-preview'); });
    // an address collected silently from the link still belongs to this person
    this.querySelectorAll('[data-feedback], [data-additional], [data-email]:not([type="hidden"])').forEach(function (field) {
      field.value = '';
      el.autoGrow(field);
      el.count(field);
    });
    this.querySelectorAll('[data-chip]').forEach(function (chip) { chip.setAttribute('aria-pressed', 'false'); });

    this.paintReaction(null);
    this.paintFace(null);
    var host = this.querySelector('[data-burst]');
    if (host) host.innerHTML = '';

    this.started = false;
    this.flow = this.defaultFlow();
    this.pos = this.flow.length - 1;
    this.labelButtons();
    this.goTo(0);
  }
}

customElements.define('rating-feedback-form', RatingFeedbackForm);
  })();
